import PropTypes from 'prop-types';

import { Box } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import GenericFormContainer from '../Forms/GenericFormContainer';

import InfoComponent from '../Tooltip/InfoComponent';

import { isAdmin } from '../../utils';
import {
  lengthValidation120,
  lengthValidation1000,
  maxFileSize,
  notNullOrUndefined,
  onlyNumeric,
  required,
} from '../Forms/utils';
import {
  getDisabledScheduleValues,
  scheduleValidation,
} from '../Schedule/utils';
import {
  maxDaysToBookLimit,
  minDaysToBookLimit,
  simultaneousReservationsLimit,
} from './utils';

import { AMENITY_ACTIVE, AMENITY_NOTES } from '../Tooltip/constants';

import {
  CHECKBOX,
  IMAGE_UPLOAD,
  MESSAGE,
  ONE_LINE_TEXT_FIELD,
  REACT_SELECT_INPUT,
  SCHEDULE,
  STYLED_TEXT,
  TIME_PICKER,
} from '../Forms/fieldTypes';
import { amenityTimeConstraintsValidation } from './timePickerValidation';

const validate = (values) => scheduleValidation(values?.schedule);

const LabelWrapper = ({ children }) => (
  <Box display="flex" alignItems="center">
    <Box display="flex" alignItems="center">
      {children}
    </Box>
  </Box>
);

LabelWrapper.propTypes = {
  children: PropTypes.node.isRequired,
};

const AmenityFormComponent = (props) => {
  const { t } = useTransl();

  const {
    isRentalBuilding,
    startTime,
    endTime,
    timezone,
    isCreate,
    onSubmit,
    onLoad,
    form,
    initialValues: values,
    buildingOptions,
    isReservationAgreements = false,
    connectedDoorsOptions,
    connectedDoors,
  } = props;

  const initialValues = {
    ...values,
    rentalAgreementTitle: values?.rentalAgreement?.title,
    rentalAgreementText: values?.rentalAgreement?.text,
  };

  const NotesLabel = (
    <LabelWrapper>
      <div>{t('amenity.extraReservationInfo')}</div>
      <InfoComponent type={AMENITY_NOTES} />
    </LabelWrapper>
  );

  const ActiveLabel = (
    <LabelWrapper>
      <div>{t('amenity.active')}</div>
      <InfoComponent type={AMENITY_ACTIVE} />
    </LabelWrapper>
  );

  const formData = [
    {
      keyName: 'building',
      label: t('title.building'),
      fieldType: REACT_SELECT_INPUT,
      options: buildingOptions,
      validate: [required],
      disabled: buildingOptions?.length === 1,
    },
    {
      keyName: 'name',
      label: t('title.amenityName'),
      fieldType: STYLED_TEXT,
      validate: [required, lengthValidation120],
    },
    {
      keyName: 'images',
      label: t('title.images'),
      fieldType: IMAGE_UPLOAD,
      validate: [maxFileSize],
    },
    {
      keyName: 'requiresApproval',
      label: t('title.requiresApproval'),
      headerLabel: t('title.reservationApprovals'),
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'schedule',
      label: timezone ? (
        <span>{t('title.availability')}*</span>
      ) : (
        t('title.availability')
      ),
      fieldType: SCHEDULE,
      validate: [required],
      getDisabledScheduleValues,
      timezone,
    },
    {
      keyName: 'connectedDoors',
      label: t('title.connectedDoors'),
      fieldType: REACT_SELECT_INPUT,
      options: connectedDoorsOptions,
      validate: [],
      multi: true,
      isHidden: !isAdmin(),
    },
    {
      keyName: 'forceAmenitySchedule',
      label: t('title.forceAmenitySchedule'),
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
      isHidden: !isAdmin() || !connectedDoors?.length,
    },
    {
      keyName: 'active',
      label: ActiveLabel,
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'minimumReservationTime',
      label: t('title.minimumReservationTime'),
      fieldType: TIME_PICKER,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'startTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'startTime',
          startTime,
          endTime,
          selectedHour,
        ),
      validate: [amenityTimeConstraintsValidation],
    },
    {
      keyName: 'maximumReservationTime',
      label: t('title.maximumReservationTime'),
      fieldType: TIME_PICKER,
      getDisabledHours: () =>
        getDisabledScheduleValues('hour', 'endTime', startTime, endTime),
      getDisabledMinutes: (selectedHour) =>
        getDisabledScheduleValues(
          'minute',
          'endTime',
          startTime,
          endTime,
          selectedHour,
        ),
      validate: [amenityTimeConstraintsValidation],
    },
    {
      keyName: 'notes',
      label: NotesLabel,
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [lengthValidation1000],
    },
    {
      keyName: 'rentalAgreementRequired',
      label: t('title.rentalAgreementRequired'),
      fieldType: CHECKBOX,
      validate: [notNullOrUndefined],
    },
    {
      keyName: 'rentalAgreementTitle',
      label: t('title.rentalAgreementTitle'),
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [lengthValidation120],
      isHidden: !isReservationAgreements,
    },
    {
      keyName: 'rentalAgreementText',
      label: t('title.rentalAgreementText'),
      fieldType: STYLED_TEXT,
      multiline: true,
      validate: [lengthValidation1000],
      isHidden: !isReservationAgreements,
      minRows: 5,
    },
    {
      keyName: 'message',
      fieldType: MESSAGE,
      value: t('title.additionalSettings'),
    },
    {
      keyName: 'minimumDaysBeforeReservation',
      label: t('title.minimumDaysBeforeReservation'),
      placeholder: t('title.noLimit'),
      fieldType: ONE_LINE_TEXT_FIELD,
      validate: [onlyNumeric, minDaysToBookLimit],
    },
    {
      keyName: 'maximumDaysBeforeReservation',
      label: t('title.maximumDaysBeforeReservation'),
      placeholder: t('title.noLimit'),
      fieldType: ONE_LINE_TEXT_FIELD,
      validate: [onlyNumeric, maxDaysToBookLimit],
    },
    {
      keyName: 'ownerNumReservationsCount',
      label: t('title.ownerNumReservationsCount'),
      placeholder: t('title.noLimit'),
      fieldType: ONE_LINE_TEXT_FIELD,
      validate: [onlyNumeric],
      disabled: isRentalBuilding,
    },
    {
      keyName: 'tenantNumReservationsCount',
      label: t('title.tenantNumReservationsCount'),
      placeholder: t('title.noLimit'),
      fieldType: ONE_LINE_TEXT_FIELD,
      validate: [onlyNumeric],
    },
    {
      keyName: 'simultaneousNumberOfReservations',
      label: t('title.simultaneousNumberOfReservations'),
      fieldType: ONE_LINE_TEXT_FIELD,
      validate: [required, onlyNumeric, simultaneousReservationsLimit],
    },
  ];

  return (
    <GenericFormContainer
      form={form}
      formData={formData}
      onSubmit={onSubmit}
      initialValues={initialValues}
      onLoad={onLoad}
      title={isCreate ? t('amenity.addAmenity') : t('amenity.editAmenity')}
      submitText={t('amenity.saveAmenity')}
      validate={validate}
      isFullWidthForm
      enableReinitialize
    />
  );
};

AmenityFormComponent.defaultProps = {
  buildingOptions: [],
  startTime: null,
  endTime: null,
  timezone: '',
  connectedDoorsOptions: [],
};

AmenityFormComponent.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  buildingOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  isReservationAgreements: PropTypes.bool,
  isRentalBuilding: PropTypes.bool.isRequired,
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  endTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  timezone: PropTypes.string,
  isCreate: PropTypes.bool.isRequired,
  connectedDoorsOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object)),
  connectedDoors: PropTypes.instanceOf(Array),
};

export default AmenityFormComponent;
